<template>
  <section
    class="what-i-do"
    ref="sectionRef"
    id="whatido"
    @mousemove="handleSectionMouseMove"
    @mouseleave="handleSectionMouseLeave"
  >
    <div class="flex justify-center items-center h-full">
      <div
        class="bioma-insects-overlay"
        :class="{ 'bioma-insects-overlay--visible': isCreativeHovered }"
      >
        <BiomaInsects ref="biomaRef" />
      </div>

      <div class="what-i-do__container">
        <div class="what-i-do__header" ref="headerRef">
          <h2 class="what-i-do__title">
            {{ $t('page.whatIDo.title.first') }}
            <span class="what-i-do__title-accent">{{ $t('page.whatIDo.title.highlight') }}</span>
          </h2>
        </div>

        <div class="what-i-do__services" ref="servicesRef">
          <div
            v-for="(service, index) in services"
            :key="index"
            class="service-item"
            :class="{ 
              'service-item--has-frame': service.hasFrame,
              'service-item--active': isMobileModalOpen && currentServiceIndex === index,
              'is-hovered': hoveredServices.has(index)
            }"
            :ref="(el) => (serviceItemRefs[index] = el)"
            @mouseenter="handleMouseEnter(index, $event)"
            @mouseleave="handleMouseLeave(index)"
            @mousemove="handleServiceMouseMove($event, index)"
            @click="handleServiceClick(index)"
          >
            <span v-if="service.hasTags" class="tag tag--open">&lt;</span>
            <span v-if="service.hasTags" class="tag tag--close">/&gt;</span>

            <div v-if="service.hasDesignEffect" class="design-effect">
              <div class="grid-line grid-line--h"></div>
              <div class="grid-line grid-line--v"></div>

              <div class="crop-mark crop-mark--tl"></div>
              <div class="crop-mark crop-mark--tr"></div>
              <div class="crop-mark crop-mark--bl"></div>
              <div class="crop-mark crop-mark--br"></div>

              <span class="design-script">Design</span>

              <div class="bubbles-container">
                <div class="color-bubble color-bubble--1"></div>
                <div class="color-bubble color-bubble--2"></div>
                <div class="color-bubble color-bubble--3"></div>
              </div>
            </div>

            <div v-if="service.hasCreativeEffect" class="creative-effect">
              <div class="creative-spotlight" :style="spotlightStyle"></div>
            </div>

            <div v-if="service.hasFrame" class="service-item__frame">
              <span class="service-item__frame-corner service-item__frame-corner--tl"></span>
              <span class="service-item__frame-corner service-item__frame-corner--tr"></span>
              <span class="service-item__frame-corner service-item__frame-corner--bl"></span>
              <span class="service-item__frame-corner service-item__frame-corner--br"></span>
              <span class="service-item__frame-size">70 x 7</span>
            </div>

            <h3 class="service-item__title" :class="{ 'creative-glow': service.hasCreativeEffect }">
              <span class="service-item__text service-item__text--bg" aria-hidden="true">{{
                service.title
              }}</span>
              <span
                class="service-item__text service-item__text--fill"
                :style="[{ clipPath: `inset(0 ${100 - service.progress}% 0 0)` }]"
                >{{ service.title }}</span
              >
            </h3>
          </div>
        </div>
      </div>

      <div
        class="service-tooltip"
        :class="{ 'service-tooltip--visible': isTooltipVisible }"
        :style="tooltipStyle"
        ref="tooltipRef"
      >
        <span class="service-tooltip__icon">✦</span>
        <div class="service-tooltip__content">
          <div class="service-tooltip__text-wrapper" ref="tooltipTextWrapperRef">
            <span class="service-tooltip__text" ref="tooltipTextRef">{{ currentTooltipText }}</span>
          </div>
        </div>
      </div>

      <div
        class="custom-cursor"
        ref="cursorRef"
        :class="{
          'custom-cursor--visible': isCursorVisible && cursorState !== 'hidden',
          'custom-cursor--pointer': cursorState === 'pointer',
        }"
      >
        <img
          v-if="cursorState === 'pointer'"
          src="@/assets/images/cursor-pointer.svg"
          alt="Pointer"
          class="custom-cursor__icon"
        />
        <div v-else class="custom-cursor__dot"></div>
      </div>

      <TiltIndicator :show="showTiltIndicator" />

      <!-- Mobile Tap Indicator (Floating Pill) -->
      <div 
        class="tap-indicator" 
        :class="{ 'tap-indicator--visible': !isDesktop && !hasInteracted && !showTiltIndicator }"
      >
        <Tap02Icon class="tap-icon" />
        <span class="tap-text">{{ $t('common.tapToExplore') }}</span>
      </div>

      <!-- Mobile Service Modal -->
      <Teleport to="body">
        <Transition name="modal-slide">
          <div 
            v-if="!isDesktop && isMobileModalOpen && currentServiceIndex !== null" 
            class="mobile-service-modal-backdrop"
            @click="closeMobileModal"
          >
            <div class="mobile-service-modal" @click.stop>
              <div class="mobile-service-modal__header">
                <h3 class="mobile-service-modal__title">{{ services[currentServiceIndex].title }}</h3>
                <button class="mobile-service-modal__close" @click="closeMobileModal">
                  <Cancel01Icon size="24" style="color: var(--color-text-muted)" />
                </button>
              </div>
              <div class="mobile-service-modal__body">
                <p class="mobile-service-modal__description">
                  {{ services[currentServiceIndex].description }}
                </p>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'

import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useI18n } from 'vue-i18n'
import BiomaInsects from './BiomaInsects.vue'
import TiltIndicator from '@/components/common/TiltIndicator.vue'
import { useDevice } from '@/composables/useDevice'
import { Tap02Icon, Cancel01Icon } from 'hugeicons-vue'

gsap.registerPlugin(ScrollTrigger)

const { t } = useI18n()

const SCROLL_DEACTIVATION_THRESHOLD = 200
const MOBILE_SCROLL_OFFSET_FACTOR = 0.45

const sectionRef = ref(null)
const servicesRef = ref(null)
const headerRef = ref(null)
const serviceItemRefs = ref([])
const biomaRef = ref(null) // Ref for BiomaInsects

// Tooltip refs
const tooltipRef = ref(null)
const tooltipTextRef = ref(null)
const tooltipTextWrapperRef = ref(null)

// Tooltip state
const hoveredServices = ref(new Set())
const currentServiceIndex = ref(null)
const tooltipX = ref(0)
const tooltipY = ref(0)
const hasInteracted = ref(false)
const isMobileModalOpen = ref(false)

// Custom Cursor state
const cursorRef = ref(null)
const isCursorVisible = ref(false)
const cursorX = ref(0)
const cursorY = ref(0)
const cursorState = ref('default')

// Creative Spotlight State
const spotlightX = ref(0)
const spotlightY = ref(0)

// Tilt Indicator
const showTiltIndicator = ref(false)
const { isDesktop } = useDevice()

const isTooltipVisible = computed(() => hoveredServices.value.size > 0)

const currentTooltipText = computed(() => {
  if (currentServiceIndex.value !== null && services[currentServiceIndex.value]) {
    return services[currentServiceIndex.value].description
  }
  return ''
})

const isCreativeHovered = computed(() => {
  const creativeIndex = services.findIndex((s) => s.hasCreativeEffect)
  if (creativeIndex === -1) return false
  
  if (isDesktop.value) {
    return hoveredServices.value.has(creativeIndex)
  } else {
    // On mobile, only active if modal is open for this service
    return isMobileModalOpen.value && currentServiceIndex.value === creativeIndex
  }
})

const tooltipStyle = computed(() => ({
  left: `${tooltipX.value}px`,
  top: `${tooltipY.value}px`,
}))

const spotlightStyle = computed(() => ({
  '--spotlight-x': `${spotlightX.value}px`,
  '--spotlight-y': `${spotlightY.value}px`,
}))

const services = reactive([
  {
    title: computed(() => t('page.whatIDo.services.uiUx.title')),
    progress: 0,
    description: computed(() => t('page.whatIDo.services.uiUx.description')),
    hasFrame: true,
  },
  {
    title: computed(() => t('page.whatIDo.services.frontend.title')),
    progress: 0,
    description: computed(() => t('page.whatIDo.services.frontend.description')),
    hasFrame: false,
    hasTags: true,
  },
  {
    title: computed(() => t('page.whatIDo.services.design.title')),
    progress: 0,
    description: computed(() => t('page.whatIDo.services.design.description')),
    hasFrame: false,
    hasDesignEffect: true,
  },
  {
    title: computed(() => t('page.whatIDo.services.creative.title')),
    progress: 0,
    description: computed(() => t('page.whatIDo.services.creative.description')),
    hasFrame: false,
    hasCreativeEffect: true,
  },
  
])

const isAutoScrolling = ref(false)

const handleServiceClick = (index) => {
  if (!isDesktop.value) {
    if (currentServiceIndex.value === index && isMobileModalOpen.value) {
      closeMobileModal()
      return
    }

    currentServiceIndex.value = index
    isMobileModalOpen.value = true
    hasInteracted.value = true
    isAutoScrolling.value = true

    nextTick(() => {
      const item = serviceItemRefs.value[index]
      if (item) {
        const rect = item.getBoundingClientRect()
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const absoluteTop = rect.top + scrollTop
        // Position element at ~45% of viewport height (higher up) to leave room for bottom modal
        const offset = window.innerHeight * MOBILE_SCROLL_OFFSET_FACTOR 
        
        window.scrollTo({
          top: absoluteTop - offset,
          behavior: 'smooth'
        })
        
        setTimeout(() => {
            isAutoScrolling.value = false
        }, 800)
      } else {
        isAutoScrolling.value = false
      }
    })
  }
}

const closeMobileModal = () => {
  isMobileModalOpen.value = false
}

const onScrollCloseModal = () => {
  if (isMobileModalOpen.value && !isAutoScrolling.value) {
    closeMobileModal()
  }
}

const onScrollDesktop = () => {
  if (!isDesktop.value || currentServiceIndex.value === null) return
  
  const item = serviceItemRefs.value[currentServiceIndex.value]
  if (!item) return
  
  const rect = item.getBoundingClientRect()
  const mouseY = cursorY.value
  
  const distTop = rect.top - mouseY
  const distBottom = mouseY - rect.bottom
  
  if (distTop > SCROLL_DEACTIVATION_THRESHOLD || distBottom > SCROLL_DEACTIVATION_THRESHOLD) {
    handleMouseLeave(currentServiceIndex.value)
  }
}

watch(isMobileModalOpen, (val) => {
  if (val) {
    window.addEventListener('scroll', onScrollCloseModal, { passive: true })
  } else {
    window.removeEventListener('scroll', onScrollCloseModal)
  }
})

const animateTextChange = (newIndex) => {
  if (!tooltipTextRef.value) return
  if (currentServiceIndex.value === newIndex) return

  gsap.to(tooltipTextRef.value, {
    y: -15,
    opacity: 0,
    duration: 0.15,
    ease: 'power2.in',
    onComplete: () => {
      currentServiceIndex.value = newIndex
      nextTick(() => {
        gsap.fromTo(
          tooltipTextRef.value,
          { y: 15, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.2,
            ease: 'power2.out',
          }
        )
      })
    },
  })
}

const handleMouseEnter = (index, event) => {
  // Mark as interacted on first touch/hover
  if (!hasInteracted.value) {
    hasInteracted.value = true
  }

  hoveredServices.value.add(index)
  tooltipX.value = event.clientX + 20
  tooltipY.value = event.clientY + 10

  isCursorVisible.value = true
  gsap.to(cursorRef.value, {
    scale: 1,
    duration: 0.4,
    ease: 'back.out(1.7)',
  })

  if (currentServiceIndex.value === null) {
    currentServiceIndex.value = index
  } else {
    animateTextChange(index)
  }

  // Show Tilt Indicator if Creative service (index 3) is active/hovered and not desktop
  if (services[index].hasCreativeEffect && !isDesktop.value) {
    showTiltIndicator.value = true
  } else {
    showTiltIndicator.value = false
  }

  const item = serviceItemRefs.value[index]
  if (item) {
    gsap.to(item.querySelector('.service-item__title'), {
      scale: 1.05,
      duration: 0.4,
      ease: 'power4.out',
    })
  }

  if (services[index].title === 'UI/UX' && isDesktop.value) {
    cursorState.value = 'pointer'
  } else if (services[index].hasCreativeEffect) {
    cursorState.value = 'hidden'
  } else {
    cursorState.value = 'default'
  }
}


const handleMouseLeave = (index) => {
  hoveredServices.value.delete(index)

  const item = serviceItemRefs.value[index]
  if (item) {
    gsap.to(item.querySelector('.service-item__title'), {
      scale: 1,
      duration: 0.3,
      ease: 'power2.out',
    })
  }

  cursorState.value = 'default'
  isCursorVisible.value = false
  gsap.to(cursorRef.value, {
    scale: 0,
    duration: 0.3,
    ease: 'power2.in',
  })

  if (hoveredServices.value.size === 0) {
    setTimeout(() => {
      if (hoveredServices.value.size === 0) {
        currentServiceIndex.value = null
      }
    }, 100)
  }
}

const handleServiceMouseMove = (event, index) => {
  tooltipX.value = event.clientX + 20
  tooltipY.value = event.clientY + 10

  if (services[index].hasCreativeEffect) {
    const item = event.currentTarget
    if (!item) return

    const rect = item.getBoundingClientRect()
    spotlightX.value = event.clientX - rect.left
    spotlightY.value = event.clientY - rect.top
  }
}



const handleSectionMouseLeave = () => {
  isCursorVisible.value = false
  gsap.to(cursorRef.value, {
    scale: 0,
    duration: 0.3,
    ease: 'power2.in',
  })
}

const handleSectionMouseMove = (event) => {
  if (!sectionRef.value || !cursorRef.value) return

  cursorX.value = event.clientX
  cursorY.value = event.clientY

  gsap.to(cursorRef.value, {
    x: cursorX.value,
    y: cursorY.value,
    duration: 0.1,
    ease: 'power2.out',
  })
}

let scrollTriggerInstance = null

onMounted(() => {
  setTimeout(() => {
    if (!sectionRef.value) return

    if (window.lenis) {
      window.lenis.on('scroll', ScrollTrigger.update)
      gsap.ticker.add((time) => {
        window.lenis?.raf(time * 1000)
      })
      gsap.ticker.lagSmoothing(0)
    }

    scrollTriggerInstance = ScrollTrigger.create({
      trigger: sectionRef.value,
      start: 'top 50%',
      end: 'top 20%',
      scrub: 0.8,
      onUpdate: (self) => {
        const totalProgress = self.progress * 100
        const itemCount = services.length
        const progressPerItem = 100 / itemCount

        services.forEach((service, index) => {
          const itemStart = index * progressPerItem
          const itemEnd = (index + 1) * progressPerItem

          if (totalProgress <= itemStart) {
            service.progress = 0
          } else if (totalProgress >= itemEnd) {
            service.progress = 100
          } else {
            const itemProgress = ((totalProgress - itemStart) / progressPerItem) * 100
            service.progress = Math.min(100, itemProgress)
          }
        })
      },
      // Reset interaction states when leaving section
      // Removed onLeave/onLeaveBack from here as this trigger is too short
    })

    // Separate trigger for Section Visibility (Reset State)
    ScrollTrigger.create({
      trigger: sectionRef.value,
      start: 'top bottom', // When top enters viewport
      end: 'bottom top', // When CENTER leaves viewport (active states cleared sooner)
      onLeave: resetSectionState,
      onLeaveBack: resetSectionState,
    })

    if (headerRef.value) {
      gsap.from(headerRef.value.children, {
        y: 30,
        opacity: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: headerRef.value,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      })
    }
  }, 100)

  // Init Device Orientation
  if (window.DeviceOrientationEvent) {
    window.addEventListener('deviceorientation', handleDeviceOrientation)
  }
  
  window.addEventListener('scroll', onScrollDesktop, { passive: true })
})

onUnmounted(() => {
  if (scrollTriggerInstance) scrollTriggerInstance.kill()
  window.removeEventListener('deviceorientation', handleDeviceOrientation)
  window.removeEventListener('scroll', onScrollDesktop)
})

const resetSectionState = () => {
  // Clear all hover states
  hoveredServices.value.clear()
  currentServiceIndex.value = null
  isCursorVisible.value = false
  cursorState.value = 'default'

  // Reset service items scales
  serviceItemRefs.value.forEach((item) => {
    if (item) {
      gsap.to(item.querySelector('.service-item__title'), {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
      })
    }
  })
}

// Device Orientation for Fireflies
let initialOrientation = { beta: 0, gamma: 0 }
let isOrientationCalibrated = false

const handleDeviceOrientation = (e) => {
  if (!biomaRef.value || !sectionRef.value) return

  // Only process if section is within view (simple check)
  // Actually, we want it to work whenever visible.

  if (!isOrientationCalibrated) {
    initialOrientation = { beta: e.beta || 0, gamma: e.gamma || 0 }
    isOrientationCalibrated = true
    return
  }

  const gamma = (e.gamma || 0) - initialOrientation.gamma // Left/Right tilt
  const beta = (e.beta || 0) - initialOrientation.beta // Front/Back tilt

  // Map tilt to screen coordinates
  // gamma: -45 to 45 -> width
  // beta: -45 to 45 -> height

  const { innerWidth, innerHeight } = window
  const cx = innerWidth / 2
  const cy = innerHeight / 2

  // Sensitivity factor
  const sensitivity = 15

  const targetX = cx + gamma * sensitivity
  const targetY = cy + beta * sensitivity

  // Send to BiomaInsects
  biomaRef.value.setInteractionPoint(targetX, targetY)
}
</script>

<style scoped>
.what-i-do {
  @apply relative h-screen;
  background: var(--color-deep-black);
  overflow: hidden;
}

.bioma-insects-overlay {
  @apply fixed inset-0 pointer-events-none;
  z-index: 20;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.bioma-insects-overlay--visible {
  opacity: 1;
}

.what-i-do__container {
  @apply w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-16;
  position: relative;
  z-index: 1;
}

/* Header */
.what-i-do__header {
  @apply mb-16 md:mb-20 text-center;
}

.what-i-do__label {
  @apply inline-block text-sm font-medium tracking-widest uppercase mb-4;
  color: var(--color-electric-purple);
}

.what-i-do__title {
  @apply text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight;
  color: var(--color-text-primary);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.what-i-do__title-accent {
  background: linear-gradient(135deg, var(--color-electric-purple) 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Services */
.what-i-do__services {
  @apply flex flex-wrap items-center justify-center gap-x-6 gap-y-8 md:gap-x-10 lg:gap-x-14;
}

.service-item {
  @apply relative;
}

.service-item.is-hovered {
  cursor: none; /* Hide system cursor only when implicitly hovered */
}

/* Figma Frame (UI/UX) */
.service-item__frame {
  @apply absolute inset-0 pointer-events-none;
  border: 2px solid #0d99ff; /* Azul Figma */
  opacity: 0;
  transform: scale(0.98);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  margin: -11px -25px;
  margin-top: 0px;
  padding: 8px 12px;
}



.service-item__frame-corner {
  @apply absolute;
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 1px;
}

.service-item__frame-corner::after {
  content: '';
  @apply absolute;
  width: 100%;
  height: 100%;
  border: 2px solid #0d99ff;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 1px;
}

.service-item__frame-corner--tl {
  top: -4px;
  left: -4px;
}

.service-item__frame-corner--tr {
  top: -4px;
  right: -4px;
}

.service-item__frame-corner--bl {
  bottom: -4px;
  left: -4px;
}

.service-item__frame-corner--br {
  bottom: -4px;
  right: -4px;
}

.service-item__frame-size {
  @apply absolute text-xs font-medium;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  background: #0d99ff;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;
}

.service-item__title {
  @apply relative;
  @apply text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl;
  @apply font-black tracking-tighter leading-none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
  will-change: transform;
}

@media (hover: hover) {
  .service-item--has-frame:hover .service-item__frame {
    opacity: 1;
    border-color: #0d99ff;
    transform: scale(1.05);
  }
}

.service-item--active.service-item--has-frame .service-item__frame {
  opacity: 1;
  border-color: #0d99ff;
  transform: scale(1.05);
}

/* Text Fill Effect */
.service-item__text {
  @apply block select-none;
}

.service-item__text--bg {
  color: var(--color-charcoal);
  opacity: 0.35;
}

:root.dark .service-item__text--bg {
  color: #252525;
  opacity: 0.6;
}

.service-item__text--fill {
  @apply absolute inset-0;
  background: linear-gradient(90deg, var(--color-ash) 0%, var(--color-text-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transition: clip-path 0.08s ease-out;
  padding-right: 0.05em;
  margin-right: -0.05em;
  margin-bottom: -5px;
}

:root.dark .service-item__text--fill {
  background: linear-gradient(90deg, #a3a3a3 0%, #f5f5f5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

@media (hover: hover) {
  .service-item:hover .service-item__text--fill {
    background: linear-gradient(90deg, var(--color-electric-purple) 0%, #a78bfa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

.service-item--active .service-item__text--fill {
  background: linear-gradient(90deg, var(--color-electric-purple) 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Tooltip */
.service-tooltip {
  @apply fixed z-50 pointer-events-none;
  @apply px-4 py-3 rounded-xl;
  @apply text-sm font-medium;
  @apply flex items-center gap-3;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-electric-purple);
  color: var(--color-text-primary);
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.2),
    0 0 20px rgba(124, 58, 237, 0.15);
  backdrop-filter: blur(12px);
  max-width: 300px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(8px) scale(0.95);
  transition:
    opacity 0.25s ease,
    transform 0.25s ease,
    visibility 0.25s ease;
}

.service-tooltip--visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}

:root.dark .service-tooltip {
  background: rgba(20, 20, 20, 0.95);
  border-color: rgba(124, 58, 237, 0.5);
  box-shadow:
    0 10px 40px rgba(0, 0, 0, 0.5),
    0 0 30px rgba(124, 58, 237, 0.2);
}

.service-tooltip__icon {
  @apply text-lg;
  color: var(--color-electric-purple);
  animation: sparkle 1.5s ease-in-out infinite;
}

@keyframes sparkle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.service-tooltip__content {
  overflow: hidden;
}

.service-tooltip__text-wrapper {
  overflow: hidden;
}

.service-tooltip__text {
  @apply block;
  line-height: 1.5;
  will-change: transform, opacity;
}

.tooltip-enter-active,
.tooltip-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.95);
}

/* Code Tags Effect */
.tag {
  @apply absolute text-4xl md:text-8xl font-mono font-bold;
  color: var(--color-electric-purple);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  pointer-events: none;
}

.tag--open {
  @apply -left-6 lg:-left-16;
}

.tag--close {
  @apply -right-[2.9rem] lg:-right-28;
}

@media (hover: hover) {
  .service-item:hover .tag--open {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }

  .service-item:hover .tag--close {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }
}

.service-item--active .tag--open {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

.service-item--active .tag--close {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

/* Design Effect */
.design-effect {
  @apply absolute inset-0 pointer-events-none;
  z-index: 5;
}

.grid-line {
  @apply absolute bg-white/20;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Mobile Service Modal */
.mobile-service-modal-backdrop {
  @apply fixed inset-0 z-[100] flex items-end justify-center;
  background: transparent;
  backdrop-filter: none;
  pointer-events: none;
}

.mobile-service-modal {
  @apply w-full max-w-lg bg-[var(--color-surface-elevated)] rounded-t-2xl p-6 pb-10;
  border-top: 1px solid var(--color-border);
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.4);
  pointer-events: auto;
}

.mobile-service-modal__header {
  @apply flex justify-between items-center mb-4;
}

.mobile-service-modal__title {
  @apply text-xl font-bold text-[var(--color-text-primary)];
}

.mobile-service-modal__close {
  @apply p-2 rounded-full hover:bg-[var(--color-surface)] transition-colors;
}

.mobile-service-modal__description {
  @apply text-[var(--color-text-secondary)] leading-relaxed text-base;
}

/* Modal Transition */
.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: opacity 0.3s ease;
}

.modal-slide-enter-active .mobile-service-modal,
.modal-slide-leave-active .mobile-service-modal {
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  opacity: 0;
}

.modal-slide-enter-from .mobile-service-modal,
.modal-slide-leave-to .mobile-service-modal {
  transform: translateY(100%);
}

/* Tap Indicator (Floating Pill) */
.tap-indicator {
  @apply absolute bottom-20 left-1/2 transform -translate-x-1/2 flex items-center gap-3 px-5 py-2.5 rounded-full;
  background: rgba(15, 23, 42, 0.85); /* Deep slate */
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  color: #fff;
  z-index: 50;
  opacity: 0;
  pointer-events: none;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translate(-50%, 20px);
}

.tap-indicator--visible {
  opacity: 1;
  pointer-events: auto;
  transform: translate(-50%, 0);
  animation: pulse-border 3s infinite;
}

.tap-icon {
  @apply text-lg;
  color: var(--color-electric-purple);
}

.tap-text {
  @apply text-xs font-bold uppercase tracking-widest;
}

@keyframes pulse-border {
  0%, 100% { border-color: rgba(255, 255, 255, 0.1); box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3); }
  50% { border-color: rgba(124, 58, 237, 0.5); box-shadow: 0 4px 25px rgba(124, 58, 237, 0.25); }
}

.grid-line--v {
  width: 1px;
  height: 100%;
  left: 50%;
  transform: scaleY(0);
  transform-origin: top;
}

.crop-mark {
  @apply absolute border-white/60;
  width: 20px;
  height: 20px;
  transition: all 0.4s ease;
  opacity: 0;
}

.crop-mark--tl {
  top: -10px;
  left: -10px;
  border-top: 1px solid var(--color-ash);
  border-left: 1px solid var(--color-ash);
}

.crop-mark--tr {
  top: -10px;
  right: -10px;
  border-top: 1px solid var(--color-ash);
  border-right: 1px solid var(--color-ash);
}

.crop-mark--bl {
  bottom: -10px;
  left: -10px;
  border-bottom: 1px solid var(--color-ash);
  border-left: 1px solid var(--color-ash);
}

.crop-mark--br {
  bottom: -10px;
  right: -10px;
  border-bottom: 1px solid var(--color-ash);
  border-right: 1px solid var(--color-ash);
}

.design-script {
  @apply absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-madi;
  font-size: 8rem;
  color: #fff;
  z-index: 20;
  opacity: 0;
  pointer-events: none;
  text-shadow: 0 0 20px rgba(0, 0, 0, 1);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  white-space: nowrap;
}

.bubbles-container {
  @apply absolute inset-0 pointer-events-none;
  display: flex;
  justify-content: center;
  align-items: center;
}

.color-bubble {
  @apply absolute rounded-full;
  opacity: 0;
  transform: scale(0);
  transition: opacity 0.3s ease;
}

.color-bubble--1 {
  width: 30px;
  height: 30px;
  background: #00ffff;
  box-shadow: 0 0 15px rgba(0, 255, 255, 0.6);
  top: -10px;
  left: 10%;
  animation: floatBubble 4s ease-in-out infinite;
}

.color-bubble--2 {
  width: 40px;
  height: 40px;
  background: #ff00ff;
  box-shadow: 0 0 15px rgba(255, 0, 255, 0.6);
  bottom: -5px;
  right: 15%;
  animation: floatBubble 5s ease-in-out infinite 0.5s;
}

.color-bubble--3 {
  width: 25px;
  height: 25px;
  background: #ffff00;
  box-shadow: 0 0 15px rgba(255, 255, 0, 0.6);
  top: 10%;
  right: 30%;
  animation: floatBubble 3.5s ease-in-out infinite 1s;
}

@keyframes floatBubble {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-10px) scale(1.1);
  }
}

@media (hover: hover) {
  .service-item:hover .grid-line {
    transform: scale(1);
  }

  .service-item:hover .crop-mark {
    opacity: 1;
  }

  .service-item:hover .design-script {
    opacity: 1;
    transform: translate(-50%, -50%) rotate(-5deg) scale(1.1);
  }

  .service-item:hover .color-bubble {
    opacity: 1;
  }
}

.service-item--active .grid-line {
  transform: scale(1);
}

.service-item--active .crop-mark {
  opacity: 1;
}

.service-item--active .design-script {
  opacity: 1;
  transform: translate(-50%, -50%) rotate(-5deg) scale(1.1);
}

.service-item--active .color-bubble {
  opacity: 1;
}

@keyframes bubblePop {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .what-i-do__services {
    @apply gap-x-4 gap-y-10;
  }

  .what-i-do__title {
    @apply text-3xl sm:text-4xl;
  }

  .service-tooltip {
    display: none;
  }
}

@media (max-width: 640px) {
  .service-item__title {
    font-size: clamp(2.5rem, 15vw, 4rem);
  }
}

/* Creative Effect */
.creative-effect {
  @apply absolute inset-0 pointer-events-none;
  z-index: 5;
  mix-blend-mode: hard-light;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.service-item:hover .creative-effect {
  opacity: 1;
}

.creative-spotlight {
  @apply absolute rounded-full;
  width: 300px;
  height: 300px;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.05) 40%,
    transparent 70%
  );
  transform: translate(-50%, -50%);
  top: var(--spotlight-y);
  left: var(--spotlight-x);
  pointer-events: none;
  filter: blur(10px);
}

.fireflies-wrapper {
  @apply absolute;
  top: var(--spotlight-y);
  left: var(--spotlight-x);
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  pointer-events: none;
}

.firefly {
  @apply absolute bg-[#ccff00];
  width: 4px;
  height: 4px;
  box-shadow: 0 0 6px #ccff00;
  opacity: 0.8;
  will-change: transform;
  top: 50%;
  left: 50%;
  border-radius: 0 !important;
}

.firefly--1 {
  animation: swarm1 4s ease-in-out infinite;
}
.firefly--2 {
  animation: swarm2 5s ease-in-out infinite;
}
.firefly--3 {
  animation: swarm3 3.5s ease-in-out infinite;
}
.firefly--4 {
  animation: swarm1 4.5s ease-in-out infinite reverse;
}
.firefly--5 {
  animation: swarm2 6s ease-in-out infinite reverse;
}
.firefly--6 {
  animation: swarm3 4s ease-in-out infinite 1s;
}

@keyframes swarm1 {
  0%,
  100% {
    transform: translate(0, 0);
  }
  33% {
    transform: translate(30px, -40px);
  }
  66% {
    transform: translate(-20px, 20px);
  }
}

@keyframes swarm2 {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(-40px, -20px);
  }
}

@keyframes swarm3 {
  0%,
  100% {
    transform: translate(0, 0);
  }
  33% {
    transform: translate(20px, 40px);
  }
  66% {
    transform: translate(-30px, -10px);
  }
}

.cursor-none {
  cursor: none !important;
}

.cursor-none * {
  cursor: none !important;
}

.custom-cursor {
  @apply fixed pointer-events-none z-[100];
  top: 0;
  left: 0;
  width: 48px;
  height: 48px;
  opacity: 0;
  visibility: hidden;
  transform: scale(0);
  margin-top: -24px;
  margin-left: -24px;
}

.custom-cursor--visible {
  opacity: 1;
  visibility: visible;
}

.custom-cursor__icon {
  width: 100%;
  height: 100%;
  transform-origin: center center;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
}
</style>
